import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth }    from '@/contexts/AuthContext';
import { getFamily, deleteFamily, setFamilyStatus } from '@/api/family';
import { listFamilyCemetery } from '@/api/cemetery';
import { deleteFamilyMember } from '@/api/familyMember';
import type { FamilyDetail } from '@/api/family';

/** sp_GetFamilyDetailsById / sp_GetFamilyMembersDetailsByFamilyId rows are bound
 *  positionally in the WinForms client — keys here are best-effort guesses with
 *  graceful fallback so the page renders regardless of exact column naming. */
const pick = (row: Record<string, unknown> | undefined, keys: string[]): string => {
  if (!row) return '—';
  for (const k of keys) {
    const v = row[k];
    if (v !== undefined && v !== null && v !== '') return String(v);
  }
  return '—';
};

const calcAge = (row: Record<string, unknown> | undefined, keys: string[]): string => {
  const raw = pick(row, keys);
  if (raw === '—') return '—';
  const dob = new Date(raw);
  if (isNaN(dob.getTime())) return '—';
  const now = new Date();
  let age = now.getFullYear() - dob.getFullYear();
  const monthDiff = now.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < dob.getDate())) age--;
  return age >= 0 ? String(age) : '—';
};

const yesNo = (row: Record<string, unknown> | undefined, keys: string[]): string => {
  if (!row) return '—';
  for (const k of keys) {
    const v = row[k];
    if (v !== undefined && v !== null) return v === true || v === 1 || v === '1' || v === 'true' ? 'Yes' : 'No';
  }
  return '—';
};

const TrashIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 6h18" />
    <path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
  </svg>
);

export default function FamilyDetailPage() {
  const { id }   = useParams<{ id: string }>();
  const { isAdmin } = useAuth();
  const navigate = useNavigate();

  const [detail,  setDetail]  = useState<FamilyDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteErr,  setDeleteErr]  = useState('');
  const [deleting,   setDeleting]   = useState(false);

  // Member pending deletion (confirmation dialog open while non-null)
  const [memberToDelete,  setMemberToDelete]  = useState<{ id: number; name: string; relationship: string } | null>(null);
  const [memberDeleteErr, setMemberDeleteErr] = useState('');
  const [memberDeleting,  setMemberDeleting]  = useState(false);

  const [statusOpen,   setStatusOpen]   = useState(false);
  const [statusErr,    setStatusErr]    = useState('');
  const [statusSaving, setStatusSaving] = useState(false);

  const fetchData = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError('');
    try {
      const result = await getFamily(parseInt(id, 10));
      setDetail(result);
    } catch {
      setError('Failed to load family details.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // How many cemetery records are linked to this family — drives the
  // "Cemetery (N)" shortcut. Fetched separately so a failure here never
  // blocks the rest of the page; the button just stays hidden.
  const [cemeteryCount, setCemeteryCount] = useState(0);
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    listFamilyCemetery(parseInt(id, 10))
      .then((rows) => { if (!cancelled) setCemeteryCount(rows.length); })
      .catch(() => { if (!cancelled) setCemeteryCount(0); });
    return () => { cancelled = true; };
  }, [id]);

  const handleMemberDelete = async () => {
    if (!id || !memberToDelete) return;
    setMemberDeleting(true);
    setMemberDeleteErr('');
    try {
      await deleteFamilyMember(parseInt(id, 10), memberToDelete.id);
      // Drop the row locally rather than refetching, so the page doesn't
      // flash back to its loading state.
      setDetail((prev) => prev && ({
        ...prev,
        members: prev.members.filter(
          (m) => Number(pick(m, ['memberId', 'member_id', 'MemberId', 'MemberID'])) !== memberToDelete.id,
        ),
      }));
      setMemberToDelete(null);
    } catch (err: any) {
      setMemberDeleteErr(err?.response?.data?.error ?? 'Delete failed.');
    } finally {
      setMemberDeleting(false);
    }
  };

  const askDeleteMember = (m: Record<string, unknown>, mId: string) => {
    setMemberDeleteErr('');
    setMemberToDelete({
      id: parseInt(mId, 10),
      name: pick(m, ['name', 'Name']),
      relationship: pick(m, ['relationship', 'Relationship']),
    });
  };

  const handleDelete = async () => {
    if (!id) return;
    setDeleting(true);
    setDeleteErr('');
    try {
      await deleteFamily(parseInt(id, 10));
      navigate('/families');
    } catch (err: any) {
      setDeleteErr(err?.response?.data?.error ?? 'Delete failed.');
    } finally {
      setDeleting(false);
    }
  };

  const handleStatusChange = async (makeActive: boolean) => {
    if (!id) return;
    setStatusSaving(true);
    setStatusErr('');
    try {
      await setFamilyStatus(parseInt(id, 10), makeActive);
      setStatusOpen(false);
      await fetchData();
    } catch (err: any) {
      setStatusErr(err?.response?.data?.error ?? 'Status update failed.');
    } finally {
      setStatusSaving(false);
    }
  };

  if (loading) {
    return <div className="p-6 text-teal text-sm">Loading family details…</div>;
  }

  if (error || !detail) {
    return (
      <div className="p-4 md:p-6">
        <div className="bg-red-50 border border-red-300 text-red-700 text-sm rounded px-4 py-2">
          {error || 'Family not found.'}
        </div>
        <Link to="/families" className="text-teal text-sm mt-4 inline-block">&larr; Back to Families</Link>
      </div>
    );
  }

  const { family, members } = detail;
  const head   = members.find((m) => /head/i.test(String(m.relationship ?? pick(m, ['Relationship']))));
  const spouse = members.find((m) => /spouse|wife|husband/i.test(String(m.relationship ?? pick(m, ['Relationship']))));
  // isactive is NULL on some legacy rows — only an explicit false means inactive.
  const isActive     = family.isactive !== false && family.isactive !== 0;
  const disabledDate = pick(family, ['disabled_date']);

  return (
    <div className="p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <Link to="/families" className="text-teal text-xs hover:underline">&larr; Back to Families</Link>
          <h1 className="text-xl font-semibold text-navy font-georgia mt-1">
            Family {pick(family, ['familyCode', 'FamilyCode', 'family_code'])}
            {!isActive && (
              <span className="ml-2 align-middle text-xs font-sans font-medium text-gray-600 bg-gray-200 px-2 py-0.5 rounded-full">
                Inactive
              </span>
            )}
          </h1>
          <p className="text-xs text-teal mt-0.5">
            {pick(family, ['anbiyamName', 'AnbiyamName', 'anbiyam_name'])} · Zone {pick(family, ['zone', 'Zone'])}
            {!isActive && disabledDate !== '—' && (
              <> · Inactive since {new Date(disabledDate).toLocaleDateString('en-GB')}</>
            )}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => navigate(`/subscriptions?familyId=${id}`)} className="btn-secondary">
            Subscriptions
          </button>
          {cemeteryCount > 0 && (
            <button
              onClick={() => navigate(
                `/cemetery?familyId=${id}&familyCode=${encodeURIComponent(pick(family, ['familyCode', 'FamilyCode', 'family_code']))}`,
              )}
              className="btn-secondary"
            >
              Cemetery ({cemeteryCount})
            </button>
          )}
          {isAdmin && (
            <>
              <button onClick={() => navigate(`/families/${id}/edit`)} className="btn-secondary">
                Edit
              </button>
              <button onClick={() => { setStatusOpen(true); setStatusErr(''); }} className="btn-secondary">
                {isActive ? 'Mark Inactive' : 'Reactivate'}
              </button>
              <button onClick={() => { setDeleteOpen(true); setDeleteErr(''); }} className="btn-danger">
                Delete
              </button>
            </>
          )}
        </div>
      </div>

      {/* Family info */}
      <div className="bg-white rounded-lg border border-grid-border shadow-sm p-6 mb-6">
        <h2 className="text-sm font-semibold text-navy font-georgia mb-4">Family Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 text-sm text-navy">
          <div>
            <span className="text-xs text-gray-400 uppercase tracking-wide block">Permanent Address</span>
            {pick(family, ['permanentAddress', 'family_permanant_address', 'PermanentAddress'])},{' '}
            {pick(family, ['permCity', 'PermCity'])}, {pick(family, ['permState', 'PermState'])} -{' '}
            {pick(family, ['permZipcode', 'PermZipcode'])}
          </div>
          <div>
            <span className="text-xs text-gray-400 uppercase tracking-wide block">Temporary Address</span>
            {pick(family, ['tempAddress', 'family_temp_address', 'TempAddress'])},{' '}
            {pick(family, ['tempCity', 'TempCity'])}, {pick(family, ['tempState', 'TempState'])} -{' '}
            {pick(family, ['tempZipcode', 'TempZipcode'])}
          </div>
          <div>
            <span className="text-xs text-gray-400 uppercase tracking-wide block">Monthly Subscription</span>
            ₹{pick(family, ['monthlySubscription', 'monthly_subscription', 'MonthlySubscription'])}
          </div>
          <div>
            <span className="text-xs text-gray-400 uppercase tracking-wide block">Multiple Cards</span>
            {yesNo(family, ['isMultipleCards', 'is_multiple_cards', 'IsMultipleCards'])}
          </div>
          <div className="md:col-span-2">
            <span className="text-xs text-gray-400 uppercase tracking-wide block">Notes</span>
            {pick(family, ['familyNotes', 'family_notes', 'FamilyNotes'])}
          </div>
        </div>
      </div>

      {/* Head & Spouse */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {[
          { title: 'Head of Family', m: head },
          { title: 'Spouse',         m: spouse },
        ].map(({ title, m }) => (
          <div key={title} className="bg-white rounded-lg border border-grid-border shadow-sm p-6">
            <h2 className="text-sm font-semibold text-navy font-georgia mb-3">{title}</h2>
            {m ? (
              <div className="space-y-1.5 text-sm text-navy">
                <div><span className="text-xs text-gray-400 uppercase tracking-wide mr-2">Name</span>{pick(m, ['name', 'Name'])}</div>
                <div><span className="text-xs text-gray-400 uppercase tracking-wide mr-2">Phone</span>{pick(m, ['phone', 'Phone'])}</div>
                <div><span className="text-xs text-gray-400 uppercase tracking-wide mr-2">Email</span>{pick(m, ['email', 'Email'])}</div>
              </div>
            ) : (
              <p className="text-xs text-gray-400">No record</p>
            )}
          </div>
        ))}
      </div>

      {/* Members */}
      <div className="bg-white rounded-lg border border-grid-border shadow-sm overflow-x-auto">
        <div className="px-6 py-4 border-b border-grid-border flex items-center justify-between">
          <h2 className="text-sm font-semibold text-navy font-georgia">Family Members</h2>
          {isAdmin && (
            <button onClick={() => navigate(`/families/${id}/members/new`)} className="btn-primary !text-xs !px-3 !py-1.5">
              + Add Member
            </button>
          )}
        </div>
        <div className="hidden sm:block">
        <table className="w-full text-sm text-navy font-georgia">
          <thead>
            <tr className="bg-navy text-offwhite text-left text-xs uppercase tracking-wide">
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Relationship</th>
              <th className="px-4 py-3">Gender</th>
              <th className="px-4 py-3">Age</th>
              <th className="px-4 py-3">Phone</th>
              <th className="px-4 py-3">Occupation</th>
              {isAdmin && <th className="px-4 py-3 text-right">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {members.length === 0 ? (
              <tr>
                <td colSpan={isAdmin ? 7 : 6} className="text-center py-10 text-gray-400 text-xs">
                  No members recorded.
                </td>
              </tr>
            ) : (
              members.map((m, idx) => {
                const mId = pick(m, ['memberId', 'member_id', 'MemberId', 'MemberID']);
                return (
                <tr key={mId || String(idx)} className={idx % 2 === 0 ? 'bg-white' : 'bg-offwhite/50'}>
                  <td className="px-4 py-3 font-medium">
                    {pick(m, ['name', 'Name'])}
                    {pick(m, ['member_status', 'status', 'Status']) === 'Deceased' && (
                      <span className="ml-1.5 text-xs font-normal text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">Deceased</span>
                    )}
                  </td>
                  <td className="px-4 py-3">{pick(m, ['relationship', 'Relationship'])}</td>
                  <td className="px-4 py-3">{pick(m, ['gender', 'Gender'])}</td>
                  <td className="px-4 py-3">{calcAge(m, ['dob', 'DOB'])}</td>
                  <td className="px-4 py-3">{pick(m, ['phone', 'Phone'])}</td>
                  <td className="px-4 py-3">{pick(m, ['occupation', 'Occupation'])}</td>
                  {isAdmin && (
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button
                        onClick={() => navigate(`/families/${id}/members/${mId}/edit`)}
                        className="btn-secondary !px-2 !py-1 text-xs"
                        disabled={!mId}
                        title={mId ? 'Edit member' : 'Member ID not available'}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => askDeleteMember(m, mId)}
                        className="btn-danger !px-2 !py-1 text-xs ml-2 inline-flex items-center align-middle"
                        disabled={!mId || mId === '—'}
                        title="Delete member"
                        aria-label={`Delete ${pick(m, ['name', 'Name'])}`}
                      >
                        <TrashIcon />
                      </button>
                    </td>
                  )}
                </tr>
                );
              })
            )}
          </tbody>
        </table>
        </div>

        {/* Mobile: compact cards */}
        <div className="sm:hidden divide-y divide-grid-border">
          {members.length === 0 ? (
            <p className="text-center py-10 text-gray-400 text-xs">No members recorded.</p>
          ) : (
            members.map((m, idx) => {
              const mId = pick(m, ['memberId', 'member_id', 'MemberId', 'MemberID']);
              return (
                <div key={mId || String(idx)} className="p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-medium text-navy font-georgia truncate">
                        {pick(m, ['name', 'Name'])}
                        {pick(m, ['member_status', 'status', 'Status']) === 'Deceased' && (
                          <span className="ml-1.5 text-xs font-normal text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">Deceased</span>
                        )}
                      </p>
                      <p className="text-xs text-teal font-georgia">{pick(m, ['relationship', 'Relationship'])}</p>
                    </div>
                    {isAdmin && (
                      <div className="flex gap-1.5 flex-shrink-0">
                        <button
                          onClick={() => navigate(`/families/${id}/members/${mId}/edit`)}
                          className="btn-secondary !py-1 !px-2 text-xs"
                          disabled={!mId}
                          title={mId ? 'Edit member' : 'Member ID not available'}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => askDeleteMember(m, mId)}
                          className="btn-danger !py-1 !px-2 text-xs inline-flex items-center"
                          disabled={!mId || mId === '—'}
                          title="Delete member"
                          aria-label={`Delete ${pick(m, ['name', 'Name'])}`}
                        >
                          <TrashIcon />
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    <span className="text-xs bg-offwhite text-navy rounded-full px-2 py-0.5 font-georgia">{pick(m, ['gender', 'Gender'])}</span>
                    <span className="text-xs bg-offwhite text-navy rounded-full px-2 py-0.5 font-georgia">Age {calcAge(m, ['dob', 'DOB'])}</span>
                    {pick(m, ['phone', 'Phone']) !== '—' && (
                      <span className="text-xs bg-offwhite text-navy rounded-full px-2 py-0.5 font-georgia">{pick(m, ['phone', 'Phone'])}</span>
                    )}
                    {pick(m, ['occupation', 'Occupation']) !== '—' && (
                      <span className="text-xs bg-offwhite text-navy rounded-full px-2 py-0.5 font-georgia">{pick(m, ['occupation', 'Occupation'])}</span>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Active / inactive confirmation */}
      {statusOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-sm max-h-[90vh] overflow-y-auto p-6">
            <h2 className="text-base font-semibold text-navy font-georgia mb-2">
              {isActive ? 'Mark Family Inactive' : 'Reactivate Family'}
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              {isActive
                ? 'Use this when the family has left the parish or moved away. Members, subscriptions and cemetery records are kept and the family can be reactivated later.'
                : 'Mark this family as active again?'}
            </p>
            {statusErr && <p className="text-red-600 text-sm mb-3">{statusErr}</p>}
            <div className="flex justify-end gap-3">
              <button onClick={() => setStatusOpen(false)} className="btn-secondary" disabled={statusSaving}>
                Cancel
              </button>
              <button onClick={() => handleStatusChange(!isActive)} className="btn-primary" disabled={statusSaving}>
                {statusSaving ? 'Saving…' : isActive ? 'Mark Inactive' : 'Reactivate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Member delete confirmation */}
      {memberToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-sm max-h-[90vh] overflow-y-auto p-6">
            <h2 className="text-base font-semibold text-navy font-georgia mb-2">Delete Member</h2>
            <p className="text-sm text-gray-600 mb-2">
              Delete <strong>{memberToDelete.name}</strong> ({memberToDelete.relationship}) from this family?
              This cannot be undone.
            </p>
            {/head/i.test(memberToDelete.relationship) && (
              <p className="text-sm text-amber-700 mb-2">
                This is the head of the family. Set another member as Head afterwards.
              </p>
            )}
            {memberDeleteErr && <p className="text-red-600 text-sm mb-3">{memberDeleteErr}</p>}
            <div className="flex justify-end gap-3 mt-4">
              <button onClick={() => setMemberToDelete(null)} className="btn-secondary" disabled={memberDeleting}>
                Cancel
              </button>
              <button onClick={handleMemberDelete} className="btn-danger" disabled={memberDeleting}>
                {memberDeleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      {deleteOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-sm max-h-[90vh] overflow-y-auto p-6">
            <h2 className="text-base font-semibold text-navy font-georgia mb-2">Delete Family</h2>
            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to delete this family? This cannot be undone.
            </p>
            {deleteErr && <p className="text-red-600 text-sm mb-3">{deleteErr}</p>}
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeleteOpen(false)} className="btn-secondary" disabled={deleting}>
                Cancel
              </button>
              <button onClick={handleDelete} className="btn-danger" disabled={deleting}>
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
